import { useEffect, useState, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import {
  Sparkles, Loader2, CheckCircle2, ArrowRight, Wand2, Globe, Palette,
  Users, Target, MessageSquare, Mail, Phone, Building2, ChevronRight,
  Save, RefreshCw, XCircle, Instagram, Facebook, Linkedin,
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Visual Social Media Ads for your Website or Business
 *
 * URL → 60-second AI scan → editable Brand Brain → (next sprint) → campaigns.
 * Every fact is tagged {source: site|ai|user, confidence}. The owner reviews
 * and corrects the AI's inferences before generation runs.
 */
export default function SocialStudio() {
  const [params] = useSearchParams();
  const [url, setUrl] = useState(params.get("url") || "");
  const [email, setEmail] = useState("");
  const [job, setJob] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const pollRef = useRef(null);

  const stage = job?.stage;
  const stagePct = job?.progress_pct ?? 0;
  const stageLabel = job?.stage_label;
  const brand = job?.brand?.brand;
  const facts = job?.brand?.facts;

  useEffect(() => () => clearInterval(pollRef.current), []);

  const startScan = async (e) => {
    e?.preventDefault?.();
    setError("");
    if (!/^https?:\/\/|^[a-z0-9-]+\.[a-z]{2,}/i.test(url.trim())) {
      setError("Please enter a valid website like example.com"); return;
    }
    if (!email.trim() || !/@/.test(email)) {
      setError("Please enter your email to unlock 3 free previews"); return;
    }
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/social-studio/scan`,
        { url: url.trim(), email: email.trim() });
      const jobId = data.job_id;
      setJob({ id: jobId, status: "running", progress_pct: 5,
                stage: "reading_pages",
                stage_label: "Reading your website pages" });
      pollRef.current = setInterval(async () => {
        try {
          const { data: r } = await axios.get(
            `${API}/social-studio/scan/${jobId}`);
          setJob({ ...r.job, id: jobId });
          if (r.job.status === "done" || r.job.status === "failed") {
            clearInterval(pollRef.current);
            setBusy(false);
            if (r.job.status === "failed")
              toast.error(r.job.error || "Scan failed");
          }
        } catch (err) {
          console.error(err);
        }
      }, 2000);
    } catch (err) {
      setBusy(false);
      setError(err?.response?.data?.detail || "Scan failed to start");
    }
  };

  const showLanding = !job;
  const showProgress = job && job.status !== "done" && job.status !== "failed";
  const showBrand = job?.status === "done" && brand;
  const showFailed = job?.status === "failed";

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-indigo-950 to-slate-900 text-white"
         data-testid="social-studio">
      {/* Grain texture overlay */}
      <div className="fixed inset-0 opacity-[0.03] pointer-events-none mix-blend-overlay"
           style={{ backgroundImage:
             "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='80' height='80'><filter id='n'><feTurbulence baseFrequency='0.9'/></filter><rect width='100%25' height='100%25' filter='url(%23n)'/></svg>\")" }} />

      <div className="relative max-w-6xl mx-auto px-6 pt-16 pb-20">

        <div className="text-[10px] uppercase tracking-[0.35em] text-fuchsia-400 font-bold">
          WebsiteTech · AI Social Studio
        </div>
        <h1 className="mt-3 text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight max-w-3xl">
          Visual Social Media Ads
          <br />
          <span className="bg-gradient-to-r from-fuchsia-400 via-orange-300 to-amber-300 bg-clip-text text-transparent">
            for your Website or Business
          </span>
        </h1>
        <p className="mt-4 text-white/70 max-w-2xl text-lg">
          Your website already knows your business.
          Let WebsiteTech turn it into a month of branded posts, graphics,
          and campaigns — ready to review and publish.
        </p>

        {showLanding && (
          <form onSubmit={startScan}
                className="mt-10 rounded-3xl bg-white/5 ring-1 ring-white/10 backdrop-blur p-6 max-w-3xl">
            <div className="grid sm:grid-cols-[1fr_1fr_auto] gap-3">
              <label className="block">
                <span className="text-[10px] uppercase tracking-widest text-white/50 font-bold mb-1 block">
                  Your website
                </span>
                <div className="relative">
                  <Globe className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
                  <input type="text" value={url}
                          onChange={(e) => setUrl(e.target.value)}
                          placeholder="indiatourism.pro"
                          disabled={busy}
                          data-testid="social-studio-url-input"
                          className="w-full rounded-xl bg-white/10 ring-1 ring-white/10 pl-9 pr-3 py-3 text-sm placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-fuchsia-400" />
                </div>
              </label>
              <label className="block">
                <span className="text-[10px] uppercase tracking-widest text-white/50 font-bold mb-1 block">
                  Your email
                </span>
                <div className="relative">
                  <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
                  <input type="email" value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          placeholder="you@company.com"
                          disabled={busy}
                          data-testid="social-studio-email-input"
                          className="w-full rounded-xl bg-white/10 ring-1 ring-white/10 pl-9 pr-3 py-3 text-sm placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-fuchsia-400" />
                </div>
              </label>
              <div className="flex items-end">
                <button type="submit" disabled={busy}
                         data-testid="social-studio-start-btn"
                         className="w-full sm:w-auto rounded-xl bg-gradient-to-r from-fuchsia-500 to-orange-400 hover:brightness-110 text-white px-6 py-3 font-bold inline-flex items-center gap-2 disabled:opacity-50">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />}
                  Analyze site
                </button>
              </div>
            </div>
            <div className="mt-3 text-xs text-white/50">
              Free — 3 previews / day. We'll email the results.
            </div>
            {error && (
              <div className="mt-3 rounded-lg bg-rose-500/20 ring-1 ring-rose-500/30 text-rose-100 px-3 py-2 text-sm"
                   data-testid="social-studio-error">
                {error}
              </div>
            )}
          </form>
        )}

        {showProgress && (
          <ProgressPanel job={job} />
        )}

        {showFailed && (
          <div className="mt-10 rounded-2xl bg-rose-500/10 ring-1 ring-rose-500/30 text-rose-100 p-6 max-w-2xl"
               data-testid="social-studio-failed">
            <div className="font-bold flex items-center gap-2">
              <XCircle className="w-5 h-5" />
              Scan failed
            </div>
            <div className="mt-2 text-sm">{job?.error}</div>
            <button onClick={() => { setJob(null); setError(""); }}
                    data-testid="social-studio-retry"
                    className="mt-4 rounded-lg bg-white/10 hover:bg-white/20 px-3 py-1.5 text-xs font-bold">
              Try again
            </button>
          </div>
        )}

        {showBrand && (
          <BrandBrainPanel job={job} setJob={setJob} />
        )}

      </div>
    </div>
  );
}


// ---------------------------------------------------------------------------
//  Progress screen
// ---------------------------------------------------------------------------
const STAGES = [
  { key: "reading_pages",            label: "Reading your website pages", icon: Globe },
  { key: "understanding_business",   label: "Understanding your business", icon: Building2 },
  { key: "learning_visual_identity", label: "Learning your visual identity", icon: Palette },
  { key: "finding_opportunities",    label: "Finding marketing opportunities", icon: Target },
  { key: "drafting_brand_brain",     label: "Building your Brand Brain", icon: Sparkles },
  { key: "ready",                     label: "Ready", icon: CheckCircle2 },
];

function ProgressPanel({ job }) {
  const currentIdx = STAGES.findIndex(s => s.key === job.stage);
  const pct = job.progress_pct ?? 0;
  return (
    <div className="mt-10 max-w-3xl" data-testid="social-studio-progress">
      <div className="rounded-3xl bg-white/5 ring-1 ring-white/10 backdrop-blur p-8">
        <div className="text-[10px] uppercase tracking-widest text-fuchsia-400 font-bold">
          Analyzing {job.url ? new URL(job.url).hostname : "your site"}
        </div>
        <div className="mt-2 text-2xl font-bold">{job.stage_label}</div>
        {/* Progress bar */}
        <div className="mt-6 h-2 w-full rounded-full bg-white/10 overflow-hidden">
          <div className="h-full bg-gradient-to-r from-fuchsia-500 to-orange-400 transition-all duration-500"
               style={{ width: `${pct}%` }} />
        </div>
        {/* Stage list */}
        <ul className="mt-8 space-y-2">
          {STAGES.map((s, i) => {
            const done = i < currentIdx;
            const active = i === currentIdx;
            const Icon = s.icon;
            return (
              <li key={s.key}
                  data-testid={`social-studio-stage-${s.key}`}
                  className={`flex items-center gap-3 text-sm ${
                    done ? "text-emerald-300"
                     : active ? "text-white"
                     : "text-white/30"}`}>
                <div className={`w-7 h-7 rounded-full ring-1 flex items-center justify-center ${
                  done ? "bg-emerald-500/20 ring-emerald-400/40"
                   : active ? "bg-fuchsia-500/20 ring-fuchsia-400/40"
                   : "bg-white/5 ring-white/10"}`}>
                  {done ? <CheckCircle2 className="w-4 h-4" />
                   : active ? <Loader2 className="w-4 h-4 animate-spin" />
                   : <Icon className="w-3.5 h-3.5" />}
                </div>
                {s.label}
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}


// ---------------------------------------------------------------------------
//  Brand Brain editor
// ---------------------------------------------------------------------------
function BrandBrainPanel({ job, setJob }) {
  const brandData = job.brand?.brand || {};
  const [local, setLocal] = useState(brandData);
  const [busy, setBusy] = useState(false);

  useEffect(() => setLocal(brandData), [brandData?.business_name?.value]);

  const setField = (key, value) => setLocal(prev => ({
    ...prev, [key]: { ...prev[key], value, source: "user", confidence: 1.0 }
  }));

  const saveField = async (key) => {
    setBusy(true);
    try {
      await axios.put(`${API}/social-studio/brands/${job.brand_id}`,
        { fields: { [key]: local[key].value } });
      toast.success("Saved");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="mt-10 space-y-6" data-testid="social-studio-brand-brain">

      {/* Header card */}
      <div className="rounded-3xl bg-gradient-to-br from-white/10 via-white/5 to-white/5 ring-1 ring-white/10 backdrop-blur p-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-emerald-300 font-bold inline-flex items-center gap-1">
              <CheckCircle2 className="w-3.5 h-3.5" />
              Brand Brain ready
            </div>
            <h2 className="mt-2 text-3xl font-bold">
              {local?.business_name?.value || "Your business"}
            </h2>
            {local?.tagline?.value && (
              <p className="mt-1 text-white/70 italic">{local.tagline.value}</p>
            )}
          </div>
          <button onClick={() => setJob(null)}
                  data-testid="social-studio-new-scan"
                  className="rounded-lg bg-white/10 hover:bg-white/20 text-white px-3 py-2 text-xs font-bold inline-flex items-center gap-1">
            <RefreshCw className="w-3.5 h-3.5" /> Scan another site
          </button>
        </div>

        <div className="mt-6 grid sm:grid-cols-2 gap-4">
          <EditableField label="Business name" value={local?.business_name?.value}
             source={local?.business_name?.source}
             onChange={(v) => setField("business_name", v)}
             onBlur={() => saveField("business_name")}
             testid="bb-business_name" />
          <EditableField label="Industry vertical" value={local?.industry_vertical?.value}
             source={local?.industry_vertical?.source}
             onChange={(v) => setField("industry_vertical", v)}
             onBlur={() => saveField("industry_vertical")}
             testid="bb-industry_vertical" />
          <EditableField label="Tagline" value={local?.tagline?.value}
             source={local?.tagline?.source}
             onChange={(v) => setField("tagline", v)}
             onBlur={() => saveField("tagline")}
             testid="bb-tagline" />
          <EditableField label="Primary CTA" value={local?.call_to_action?.value}
             source={local?.call_to_action?.source}
             onChange={(v) => setField("call_to_action", v)}
             onBlur={() => saveField("call_to_action")}
             testid="bb-cta" />
        </div>
        {local?.one_liner?.value && (
          <div className="mt-4">
            <EditableField label="One-liner" value={local?.one_liner?.value}
               source={local?.one_liner?.source} textarea
               onChange={(v) => setField("one_liner", v)}
               onBlur={() => saveField("one_liner")}
               testid="bb-one_liner" />
          </div>
        )}
      </div>

      {/* Two-col layout */}
      <div className="grid lg:grid-cols-2 gap-6">
        <BrandListCard title="Services" icon={Wand2}
           items={local?.services?.value || []}
           source={local?.services?.source}
           onChange={(items) => setField("services", items)}
           onBlur={() => saveField("services")}
           testid="bb-services" />
        <BrandListCard title="Target customers" icon={Users}
           items={local?.target_customers?.value || []}
           source={local?.target_customers?.source}
           onChange={(items) => setField("target_customers", items)}
           onBlur={() => saveField("target_customers")}
           testid="bb-target_customers" />
        <BrandListCard title="Differentiators" icon={Target}
           items={local?.differentiators?.value || []}
           source={local?.differentiators?.source}
           onChange={(items) => setField("differentiators", items)}
           onBlur={() => saveField("differentiators")}
           testid="bb-differentiators" />
        <BrandListCard title="Content pillars" icon={MessageSquare}
           items={local?.content_pillars?.value || []}
           source={local?.content_pillars?.source}
           onChange={(items) => setField("content_pillars", items)}
           onBlur={() => saveField("content_pillars")}
           testid="bb-content_pillars" />
      </div>

      {/* Visual identity */}
      <div className="rounded-3xl bg-white/5 ring-1 ring-white/10 backdrop-blur p-6"
           data-testid="social-studio-visual">
        <div className="flex items-center gap-2 text-fuchsia-300 font-bold uppercase tracking-widest text-[10px]">
          <Palette className="w-3.5 h-3.5" /> Visual identity
        </div>
        <div className="mt-4 grid sm:grid-cols-2 gap-6">
          <div>
            <div className="text-xs text-white/50 uppercase tracking-widest font-bold mb-2">
              Colors <SourceChip source={local?.colors?.source} />
            </div>
            <div className="flex gap-2 flex-wrap">
              {(local?.colors?.value || []).length === 0 && (
                <span className="text-white/40 text-sm">Not detected — add yours later</span>
              )}
              {(local?.colors?.value || []).map((c, i) => (
                <div key={i} className="w-14 h-14 rounded-xl ring-1 ring-white/20 relative group"
                      style={{ backgroundColor: c }}
                      data-testid={`bb-color-${i}`}>
                  <div className="absolute -bottom-5 left-0 right-0 text-[10px] font-mono text-white/60 text-center">
                    {c}
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div>
            <div className="text-xs text-white/50 uppercase tracking-widest font-bold mb-2">
              Logo candidates <SourceChip source={local?.logo_candidates?.source} />
            </div>
            <div className="flex gap-2 flex-wrap">
              {(local?.logo_candidates?.value || []).length === 0 && (
                <span className="text-white/40 text-sm">No logo detected</span>
              )}
              {(local?.logo_candidates?.value || []).slice(0, 4).map((src, i) => (
                <div key={i} className="w-14 h-14 rounded-xl bg-white ring-1 ring-white/20 p-1 flex items-center justify-center"
                      data-testid={`bb-logo-${i}`}>
                  <img src={src} alt="logo" className="max-w-full max-h-full object-contain"
                        onError={(e) => e.currentTarget.style.display = 'none'} />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Contact + socials */}
      <div className="rounded-3xl bg-white/5 ring-1 ring-white/10 backdrop-blur p-6 grid sm:grid-cols-3 gap-4"
           data-testid="social-studio-contact">
        <ContactBlock icon={Mail} label="Emails" items={local?.emails?.value || []} />
        <ContactBlock icon={Phone} label="Phones" items={local?.phones?.value || []} />
        <div>
          <div className="text-xs text-white/50 uppercase tracking-widest font-bold mb-2">Social</div>
          <div className="flex gap-2">
            {["facebook", "instagram", "linkedin"].map((k) => {
              const href = (local?.social_links?.value || {})[k];
              const Icon = k === "facebook" ? Facebook : k === "instagram" ? Instagram : Linkedin;
              return href ? (
                <a key={k} href={href} target="_blank" rel="noreferrer"
                    className="w-9 h-9 rounded-lg bg-white/10 hover:bg-white/20 flex items-center justify-center"
                    data-testid={`bb-social-${k}`}>
                  <Icon className="w-4 h-4" />
                </a>
              ) : (
                <div key={k} className="w-9 h-9 rounded-lg bg-white/5 flex items-center justify-center opacity-30">
                  <Icon className="w-4 h-4" />
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Next step CTA */}
      <div className="rounded-3xl bg-gradient-to-r from-fuchsia-500/20 via-orange-400/10 to-amber-400/10 ring-1 ring-fuchsia-400/30 backdrop-blur p-6 flex flex-wrap items-center justify-between gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-fuchsia-300 font-bold">
            Turn this into an ad
          </div>
          <div className="text-lg font-bold">Generate a professional ad in 60 seconds</div>
          <div className="text-sm text-white/60">
            3 concepts · 4 formats · Short URL · QR code · Social share buttons.
          </div>
        </div>
        <a href={`/ad-builder?url=${encodeURIComponent(job?.brand?.source_url || "")}`}
            data-testid="social-studio-to-ad-builder"
            className="rounded-xl bg-gradient-to-r from-fuchsia-500 to-orange-400 hover:brightness-110 text-white px-5 py-3 font-bold inline-flex items-center gap-2">
          Build my ad <ChevronRight className="w-4 h-4" />
        </a>
      </div>

    </div>
  );
}

function EditableField({ label, value, source, onChange, onBlur, textarea, testid }) {
  const El = textarea ? "textarea" : "input";
  return (
    <div>
      <div className="text-xs text-white/50 uppercase tracking-widest font-bold mb-1 flex items-center gap-2">
        {label} <SourceChip source={source} />
      </div>
      <El value={value || ""}
           onChange={(e) => onChange(e.target.value)}
           onBlur={onBlur}
           rows={textarea ? 2 : undefined}
           data-testid={testid}
           className="w-full rounded-lg bg-white/10 ring-1 ring-white/10 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-fuchsia-400" />
    </div>
  );
}

function BrandListCard({ title, icon: Icon, items, source, onChange, onBlur, testid }) {
  const [inputVal, setInputVal] = useState("");
  const remove = (i) => onChange(items.filter((_, idx) => idx !== i));
  const add = () => {
    const v = inputVal.trim(); if (!v) return;
    onChange([...(items || []), v]);
    setInputVal("");
  };
  return (
    <div className="rounded-2xl bg-white/5 ring-1 ring-white/10 backdrop-blur p-5"
         data-testid={testid}>
      <div className="flex items-center gap-2 text-fuchsia-300 font-bold uppercase tracking-widest text-[10px]">
        <Icon className="w-3.5 h-3.5" />
        {title}
        <SourceChip source={source} />
      </div>
      <ul className="mt-3 space-y-1.5" onBlur={onBlur}>
        {(items || []).length === 0 && (
          <li className="text-white/40 text-sm italic">Nothing detected — add below</li>
        )}
        {(items || []).map((it, i) => (
          <li key={i} className="flex items-start gap-2 group text-sm">
            <span className="mt-1 w-1.5 h-1.5 rounded-full bg-fuchsia-400 shrink-0" />
            <span className="flex-1">{it}</span>
            <button onClick={() => { remove(i); onBlur?.(); }}
                    className="opacity-0 group-hover:opacity-100 text-rose-300 hover:text-rose-200"
                    data-testid={`${testid}-remove-${i}`}>
              <XCircle className="w-3.5 h-3.5" />
            </button>
          </li>
        ))}
      </ul>
      <div className="mt-3 flex gap-2">
        <input value={inputVal}
                onChange={(e) => setInputVal(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), add(), onBlur?.())}
                placeholder="Add item…"
                data-testid={`${testid}-input`}
                className="flex-1 rounded-lg bg-white/10 ring-1 ring-white/10 px-3 py-1.5 text-xs placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-fuchsia-400" />
        <button onClick={() => { add(); onBlur?.(); }}
                data-testid={`${testid}-add`}
                className="rounded-lg bg-fuchsia-500/30 hover:bg-fuchsia-500/50 text-white px-3 py-1.5 text-xs font-bold">
          Add
        </button>
      </div>
    </div>
  );
}

function ContactBlock({ icon: Icon, label, items }) {
  return (
    <div>
      <div className="text-xs text-white/50 uppercase tracking-widest font-bold mb-2 flex items-center gap-1">
        <Icon className="w-3 h-3" /> {label}
      </div>
      {(items || []).length === 0 ? (
        <div className="text-white/40 text-sm">None detected</div>
      ) : (
        <ul className="space-y-1">
          {items.map((it, i) => (
            <li key={i} className="text-sm text-white/80 truncate">{it}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

function SourceChip({ source }) {
  if (!source) return null;
  const map = {
    site: { label: "from site",    tint: "bg-emerald-500/20 text-emerald-300" },
    ai:   { label: "AI inferred",  tint: "bg-fuchsia-500/20 text-fuchsia-300" },
    user: { label: "you edited",   tint: "bg-amber-500/20 text-amber-300" },
  };
  const m = map[source]; if (!m) return null;
  return (
    <span className={`text-[8px] font-mono uppercase tracking-widest rounded-full px-1.5 py-0.5 ${m.tint}`}>
      {m.label}
    </span>
  );
}
